import { cva, type VariantProps } from "class-variance-authority";

import { Error, Info, Insight, Success, Warning } from "@/assets";
import { cn } from "@/lib/utils";

export const bannerVariants = cva(
    "gap-default p-wide text-inverse flex w-full items-start",
    {
        variants: {
            appearance: {
                default: "bg-neutral-bold-default",
                information: "bg-information-bold-default",
                announcement: "bg-announcement-bold-default",
                success: "bg-success-bold-default",
                warning: "bg-warning-bold-default",
                danger: "bg-danger-bold-default",
            },
        },
        defaultVariants: { appearance: "default" },
    },
);

export type BannerAppearance = NonNullable<
    VariantProps<typeof bannerVariants>["appearance"]
>;

const defaultIcons: Record<BannerAppearance, React.ReactNode> = {
    default: null,
    information: <Info aria-hidden="true" className="size-6 shrink-0" />,
    announcement: <Insight aria-hidden="true" className="size-6 shrink-0" />,
    success: <Success aria-hidden="true" className="size-6 shrink-0" />,
    warning: <Warning aria-hidden="true" className="size-6 shrink-0" />,
    danger: <Error aria-hidden="true" className="size-6 shrink-0" />,
};

export const Banner = ({
    className,
    appearance = "default",
    icon,
    children,
    ...props
}: React.ComponentProps<"section"> &
    VariantProps<typeof bannerVariants> & {
        icon?: React.ReactNode;
    }) => {
    const resolvedIcon = icon ?? defaultIcons[appearance ?? "default"];

    return (
        <section
            className={cn(bannerVariants({ appearance }), className)}
            {...props}
        >
            {resolvedIcon}
            <div className="flex-1">{children}</div>
        </section>
    );
};
Banner.displayName = "Banner";
